const PIXEL_ID = '1051915437359917';
const FB_EVENTS_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
    requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
  }
}

let initialized = false;

/**
 * Creates the fbq command queue and appends Meta's fbevents.js to <head>.
 * Written by hand instead of using a package loader: the common loaders anchor
 * the injected <script> to document.getElementsByTagName('script')[0], which is
 * undefined in environments that render the app without an inline script tag.
 */
function loadPixelScript(): FbqFn | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (window.fbq) return window.fbq;

  const fbq: FbqFn = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue?.push(args);
    }
  } as FbqFn;

  fbq.queue = [];
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.push = fbq;

  window.fbq = fbq;
  window._fbq = fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = FB_EVENTS_SRC;
  document.head.appendChild(script);

  return fbq;
}

/** Loads the pixel (if needed) and fires the initial PageView. Idempotent. */
function startPixel(): void {
  if (initialized) return;
  const fbq = loadPixelScript();
  if (!fbq) return;

  initialized = true;
  fbq('init', PIXEL_ID);
  fbq('track', 'PageView');
}

/**
 * Schedules the Meta Pixel to load without competing with initial render:
 * it starts as soon as the browser is idle (capped at 3s), or immediately on
 * the visitor's first scroll/click/keypress — whichever happens first. This
 * keeps the ~220 KiB fbevents.js payload off the critical rendering path.
 */
export function initMetaPixel(): void {
  if (typeof window === 'undefined' || initialized) return;

  if (window.requestIdleCallback) {
    window.requestIdleCallback(startPixel, { timeout: 3000 });
  } else {
    setTimeout(startPixel, 2000);
  }

  const events: Array<keyof WindowEventMap> = ['scroll', 'pointerdown', 'keydown'];
  const onFirstInteraction = () => {
    startPixel();
    events.forEach((evt) => window.removeEventListener(evt, onFirstInteraction));
  };
  events.forEach((evt) => window.addEventListener(evt, onFirstInteraction, { once: true, passive: true }));
}

/**
 * Tracks the "InitiateCheckout" standard event, fired when a visitor clicks a
 * buy button right before leaving for the checkout URL. Forces the pixel to
 * load immediately (rather than waiting for idle) so the event isn't lost.
 */
export function trackInitiateCheckout(): void {
  startPixel();
  window.fbq?.('track', 'InitiateCheckout');
}
